import { z } from "zod";
import { IsoDate } from "./primitives";
import { MacroSnapshot } from "./macro";
import { NewsItem, Theme } from "./news";
import { ScoredCandidate, Weights } from "./scoring";

/**
 * Bumped when the shape changes incompatibly. The web app refuses to render a
 * snapshot it does not understand rather than silently showing wrong fields.
 */
export const SNAPSHOT_SCHEMA_VERSION = 1;

/** Counts of what was and was not available, surfaced on the method page. */
export const DataQuality = z.object({
  universeSize: z.number().int().nonnegative(),
  scored: z.number().int().nonnegative(),
  excludedByGates: z.number().int().nonnegative(),
  missingFundamentals: z.number().int().nonnegative(),
  missingPriceHistory: z.number().int().nonnegative(),
  /** Provider request counts for this run, checked against free-tier caps. */
  providerRequests: z.record(z.string(), z.number().int().nonnegative()),
});
export type DataQuality = z.infer<typeof DataQuality>;

/**
 * The complete nightly artifact. This is the app's only data source: the SPA
 * reads it directly, and the chat endpoint answers exclusively from it.
 */
export const Snapshot = z.object({
  schemaVersion: z.literal(SNAPSHOT_SCHEMA_VERSION),
  generatedAt: z.string(),
  /** Trading day the data reflects, which is not necessarily the run date. */
  asOfDate: IsoDate,
  engineVersion: z.string(),
  weights: Weights,
  macro: MacroSnapshot,
  themes: z.array(Theme),
  candidates: z.array(ScoredCandidate),
  /** News referenced by candidates and themes, deduplicated. */
  news: z.array(NewsItem),
  dataQuality: DataQuality,
});
export type Snapshot = z.infer<typeof Snapshot>;
